
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LogIn, Tablet } from 'lucide-react';
import { apiService } from '../services/api';
import { User as UserType } from '../types';

interface LoginScreenProps {
  onLogin: (user: UserType) => void;
}

const LoginScreen: React.FC<LoginScreenProps> = ({ onLogin }) => {
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async () => {
    if (!name || !password) {
      setError('Vă rugăm să introduceți numele și parola');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await apiService.login({ name, password });
      
      if (response.success && response.user) {
        // Handle table accounts - redirect to table interface
        if (response.user.role === 'table') {
          localStorage.setItem('table-user', JSON.stringify(response.user));
          window.location.href = '/tablet';
        } else {
          onLogin(response.user);
        }
      } else {
        setError(response.message || 'Eroare la autentificare');
      }
    } catch (error) {
      setError('Eroare la conectarea cu serverul');
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-20 h-20 bg-primary rounded-2xl flex items-center justify-center mb-4">
            <img 
              src="/lovable-uploads/03443d9b-8b44-43df-83aa-bc7b6b127ae4.png" 
              alt="Logo" 
              className="w-12 h-12 object-contain"
            />
          </div>
          <CardTitle className="text-2xl font-bold text-gray-800">
            Pontaj Muncitori
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-lg font-medium">
              Nume:
            </Label>
            <Input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-12 text-lg"
              placeholder="Introduceți numele"
              disabled={isLoading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-lg font-medium">
              Parola:
            </Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-12 text-lg"
              placeholder="Introduceți parola"
              disabled={isLoading}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            />
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <Button
            onClick={handleLogin}
            className="w-full h-14 text-lg bg-primary hover:bg-primary/90"
            disabled={isLoading}
          >
            <LogIn className="w-5 h-5 mr-2" />
            {isLoading ? 'Se conectează...' : 'Intră în Cont'}
          </Button>

          <div className="mt-4">
            <Button
              onClick={() => window.location.href = '/tablet'}
              variant="outline"
              className="w-full h-12 text-base border-blue-300 text-blue-600 hover:bg-blue-50"
            >
              <Tablet className="w-5 h-5 mr-2" />
              Accesează Interfața Mesei
            </Button>
          </div>

        </CardContent>
      </Card>
    </div>
  );
};

export default LoginScreen;
